import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { ChevronDown } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTopics } from '../../hooks/useTaxonomy'
import { useRegions } from '../../hooks/useTaxonomy'
import { useLiveEditions } from '../../api/hooks/usePages'

const TRIGGER_CLASS =
  'flex items-center gap-1 text-sm font-sans font-semibold tracking-wider text-fg-secondary hover:text-fg transition-colors select-none outline-none'

interface TaxonomyMenuProps {
  label: string
  items: { slug: string; label: string }[]
  basePath: '/wire/topics' | '/wire/regions' | '/wire/editions'
}

function TaxonomyMenu({ label, items, basePath }: TaxonomyMenuProps) {
  const navigate = useNavigate()
  const sorted = [...items].sort((a, b) => a.label.localeCompare(b.label))

  // Split into two balanced columns
  const half = Math.ceil(sorted.length / 2)
  const col1 = sorted.slice(0, half)
  const col2 = sorted.slice(half)

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className={TRIGGER_CLASS}>
          {label.toUpperCase()}
          <ChevronDown size={12} className="mt-px" aria-hidden="true" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          sideOffset={8}
          align="start"
          className="z-50 rounded-md border border-line bg-canvas py-2 shadow-lg outline-none"
        >
          <div className="grid grid-cols-2 gap-x-2 px-1">
            {[col1, col2].map((col, ci) => (
              <div key={ci}>
                {col.map((item) => (
                  <DropdownMenu.Item
                    key={item.slug}
                    onSelect={() => navigate(`${basePath}/${item.slug}`)}
                    className="cursor-pointer rounded px-3 py-1.5 text-[12px] font-medium text-fg-secondary outline-none hover:bg-fill hover:text-fg data-[highlighted]:bg-fill data-[highlighted]:text-fg"
                  >
                    {item.label}
                  </DropdownMenu.Item>
                ))}
              </div>
            ))}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

function NavbarNavigation() {
  const { data: topics = [] } = useTopics()
  const { data: regions = [] } = useRegions()
  const { data: editions = [] } = useLiveEditions()

  return (
    <nav className="flex items-center gap-5">
      <TaxonomyMenu label="Topics"  items={topics}  basePath="/wire/topics"  />
      <TaxonomyMenu label="Regions" items={regions} basePath="/wire/regions" />
      {editions.length > 0 && (
        <TaxonomyMenu
          label="Editions"
          items={editions.map((e) => ({ slug: e.slug, label: e.title }))}
          basePath="/wire/editions"
        />
      )}
    </nav>
  )
}

export default NavbarNavigation
